import { Injectable } from '@angular/core'
import { MenuItem } from '../interfaces/api/MenuItem'
import { MenuItemType } from '../interfaces/api/MenuItemType'
import { Router } from '@angular/router'
import { environment } from '../../environments/environment'
import { AccountType } from '../interfaces/api/AccountType'
import { TranslateService } from '@ngx-translate/core'
import { ApiService } from './api.service'
import { firstValueFrom } from 'rxjs'
import { LanguageParserService } from './language-parser.service'
import { SalesforceService } from './salesforce.service'
import { ContractType } from '../interfaces/contract-type'

const MENU_ICONS: any = {
    home: 'home',
    'ctop-my-projects': 'projects',
    'ctop-bath': 'we-store-baths',
    'ctop-quoting': 'rates',
    'ctop-kitchen': 'we-store-countertops',
    'my-projects': 'projects',
    'my-samples': 'samples',
    'my-bonuses': 'commisions',
    'designer-rate': 'actios',
    colors: 'colors',
    'my-sales': 'sales',
    'retail-rewards': 'recompensas',
    'we-store': 'we-store-baths',
    'my-displays': 'exposicion',
    'my-rates': 'tarificador',
    'rates': 'tarificador',
    'quick-quote': 'rates',
    showrooms: 'city',
    'my-galleries': 'galeries',
    'e-cosentino': 'shopping-cart',
    'my-claims': 'claims',
    'my-financial-portal': 'sales',
    'technical-documentation': 'documents',
    'fabricator-rewards': 'benefits',
    'showroom-appointment': 'visit',
    'sales-support': 'support',
    partners: 'search',
    'ctop-rewards': 'recompensas',
    'c-nexus': 'c-nexus',
    'my-leads': 'clients',
    RETAIL: {
        'c-nexus': 'c-nexus',
    },
    FABRICATOR: {
        'c-nexus': 'fab-c-nexus',
    },
}

@Injectable({
    providedIn: 'root'
})
export class MenuService {

    constructor(
        private router: Router,
        private translateService: TranslateService,
        private api: ApiService,
        private languageParser: LanguageParserService,
        private canvas: SalesforceService,
    ) {
        this.translateService.get([
            'menu.ARCHITECT.colors',
            'menu.ARCHITECT.ctop-rewards',
            'menu.ARCHITECT.home',
            'menu.ARCHITECT.my-displays',
            'menu.ARCHITECT.my-claims',
            'menu.ARCHITECT.my-galleries',
            'menu.ARCHITECT.my-samples',
            'menu.ARCHITECT.online-visit',
            'menu.ARCHITECT.partners',
            'menu.ARCHITECT.sales-support',
            'menu.ARCHITECT.visualizers',
            'menu.DESIGNER.colors',
            'menu.DESIGNER.ctop-bath',
            'menu.DESIGNER.ctop-quoting',
            'menu.DESIGNER.ctop-kitchen',
            'menu.DESIGNER.ctop-my-projects',
            'menu.DESIGNER.designer-rate',
            'menu.DESIGNER.home',
            'menu.DESIGNER.inspiration',
            'menu.DESIGNER.natural-stone',
            'menu.DESIGNER.my-bonuses',
            'menu.DESIGNER.my-displays',
            'menu.DESIGNER.my-claims',
            'menu.DESIGNER.my-galleries',
            'menu.DESIGNER.my-samples',
            'menu.DESIGNER.online-visit',
            'menu.DESIGNER.partners',
            'menu.DESIGNER.sales-support',
            'menu.DESIGNER.showroom-appointment',
            'menu.DESIGNER.technical-documentation',
            'menu.DESIGNER.visualizers',
            'menu.FABRICATOR.c-academy',
            'menu.FABRICATOR.c-nexus',
            'menu.FABRICATOR.colors',
            'menu.FABRICATOR.e-cosentino',
            'menu.FABRICATOR.fabricator-rewards',
            'menu.FABRICATOR.home',
            'menu.FABRICATOR.inspiration',
            'menu.FABRICATOR.my-claims',
            'menu.FABRICATOR.my-displays',
            'menu.FABRICATOR.my-financial-portal',
            'menu.FABRICATOR.my-galleries',
            'menu.FABRICATOR.my-leads',
            'menu.FABRICATOR.my-rates',
            'menu.FABRICATOR.my-sales',
            'menu.FABRICATOR.my-samples',
            'menu.FABRICATOR.online-visit',
            'menu.FABRICATOR.quick-quote',
            'menu.FABRICATOR.technical-documentation',
            'menu.FABRICATOR.visualizers',
            'menu.INSTALLER.c-academy',
            'menu.INSTALLER.colors',
            'menu.INSTALLER.e-cosentino',
            'menu.INSTALLER.fabricator-rewards',
            'menu.INSTALLER.home',
            'menu.INSTALLER.inspiration',
            'menu.INSTALLER.my-claims',
            'menu.INSTALLER.my-displays',
            'menu.INSTALLER.my-galleries',
            'menu.INSTALLER.my-sales',
            'menu.INSTALLER.my-samples',
            'menu.INSTALLER.technical-documentation',
            'menu.INSTALLER.visualizers',
            'menu.RETAIL.b2c',
            'menu.RETAIL.blog',
            'menu.RETAIL.c-nexus',
            'menu.RETAIL.colors',
            'menu.RETAIL.home',
            'menu.RETAIL.inspiration',
            'menu.RETAIL.natural-stone',
            'menu.RETAIL.my-displays',
            'menu.RETAIL.my-galleries',
            'menu.RETAIL.my-leads',
            'menu.RETAIL.my-rates',
            'menu.RETAIL.my-sales',
            'menu.RETAIL.my-samples',
            'menu.RETAIL.online-visit',
            'menu.RETAIL.partners',
            'menu.RETAIL.quick-quote',
            'menu.RETAIL.retail-rewards',
            'menu.RETAIL.showrooms',
            'menu.RETAIL.visualizers',
            'menu.RETAIL.we-store',
        ])
    }

    public getIconFileName(item: MenuItem, accountType?: AccountType | null): string {
        if(item.accountType && item.accountType === accountType && MENU_ICONS[item.accountType]){
            return MENU_ICONS[item.accountType][item.code] || MENU_ICONS[item.code] || item.code
        }
        return MENU_ICONS[item.code] || item.code
    }

    public async goTo(item: MenuItem, skipContract = false, status?: string): Promise<boolean> {
        console.log('going to', item)
        if(environment.webComponent){
            skipContract = true;
            if(item.type == MenuItemType.ROUTE){
                item.type = MenuItemType.LINK;
                item.link = environment.frontEndUrl + "/" + item.code;
            } else if(item.requiresContract){
                const contractInfo = await firstValueFrom(this.api.getContractInfo(item.requiresContract as ContractType))
                if(!contractInfo.isSigned){
                    // If accessing an external site from an external site that requires contract redirect to home
                    item.type = MenuItemType.LINK;
                    item.link = environment.frontEndUrl;
                }
            }
        }

        if(!skipContract && item.requiresContract && !sessionStorage.getItem('is-' + item.requiresContract + '-signed')){
            this.router.navigate(['loading'], {skipLocationChange: true}).then((a) => {
                this.router.navigate(['sign-contract', item.requiresContract], {skipLocationChange: true, state: {link: item}, onSameUrlNavigation: "reload"})
            })
            return Promise.resolve(true);
        }

        if (item.type == MenuItemType.ROUTE) {
            return this.router.navigate([item.code])
        } else if (item.type == MenuItemType.LINK && item.link) {
            const link = await this.getLink(item, status);
            if(this.canvas.isCanvas() && window.top){
                window.top.postMessage({
                    type: "navigateTo",
                    communityPage: "",
                    externalUrl: link,
                    params: "",
                    newTab: false
                }, environment.salesforceUrl);
            } else {
                window.location.href = link;
            }
        } else if (item.type == MenuItemType.EXTERNAL_LINK && item.link) {
            window.location.href = await this.getLink(item, status);
        }

        return Promise.resolve(true)
    }

    private async getLink(item: MenuItem, status?: string): Promise<string> {
        const url = new URL(item.link!)
        if(item.requiresToken) {
            const tokenFromApi = await firstValueFrom(this.api.getExternalLinkToken(item.requiresToken))
            const encodedToken = tokenFromApi.token

            switch (item.requiresToken){
                case 'quickquote':
                    url.searchParams.set('sso', encodedToken)
                    break;
                case 'tarificador':
                    url.searchParams.set('token', encodedToken)
                    break;
                default:
                    url.searchParams.set('weToken', encodedToken)
            }
        }
        if(item.requiresLanguage) {
            const language = this.languageParser.getShortLanguage(this.translateService.currentLang);

            url.searchParams.set('language', language)
        }

        if (status) {
            url.searchParams.set('status', status)
        }
        return url.toString();
    }
}
